/**
 * Minimising a Boolean function of 2–4 variables: Quine–McCluskey with Petrick's method.
 *
 * Quine–McCluskey finds every prime implicant by repeatedly merging terms that differ in
 * exactly one variable. The prime implicant chart then picks out the essential ones —
 * those that alone cover some required minterm. When essential implicants do not cover
 * everything, Petrick's method writes "each remaining minterm is covered by at least one
 * of its implicants" as a product of sums, multiplies it out with absorption, and the
 * smallest products are the minimal covers: fewest terms first, then fewest literals.
 *
 * Don't-care minterms may be used to make a group bigger but never need covering.
 * Variable 0 is the most significant bit of a minterm number, matching the usual
 * Σm(…) numbering and the layout of a Karnaugh map with A as the leading variable.
 */
export interface Implicant {
  /** Bit values of the variables that are fixed. */
  value: number;
  /** A set bit marks a variable that has dropped out (a dash). */
  mask: number;
  /** Every minterm (required or don't care) it covers, ascending. */
  terms: number[];
}

export interface QmEntry {
  implicant: Implicant;
  /** Merged into a larger implicant in the next round. */
  combined: boolean;
}

export interface QmRound {
  entries: QmEntry[];
}

export interface PetrickResult {
  /** The required minterms still uncovered after the essential implicants. */
  remaining: number[];
  /** For each remaining minterm, the implicants (by index in `primes`) that cover it. */
  sums: number[][];
  /** The irredundant covers, each a list of indexes into `primes`. */
  products: number[][];
}

export interface Minimization {
  n: number;
  required: number[];
  dontCares: number[];
  rounds: QmRound[];
  primes: Implicant[];
  /** Indexes into `primes` of the essential prime implicants. */
  essential: number[];
  petrick: PetrickResult | null;
  /** Indexes into `primes` of the chosen cover. */
  cover: number[];
  /** All covers that tie for the minimum (fewest terms, then fewest literals). */
  alternatives: number[][];
  literals: number;
}

function popcount(value: number): number {
  let count = 0;
  let rest = value;
  while (rest > 0) {
    count += rest % 2;
    rest = Math.floor(rest / 2);
  }
  return count;
}

/** Bitwise operations by arithmetic, for the few bits a 2–4 variable function needs. */
function combineBits(
  a: number,
  b: number,
  rule: (x: number, y: number) => number
): number {
  let result = 0;
  for (let k = 0; k < 8; k += 1) {
    const place = 2 ** k;
    const x = Math.floor(a / place) % 2;
    const y = Math.floor(b / place) % 2;
    result += rule(x, y) * place;
  }
  return result;
}

const xorBits = (a: number, b: number) =>
  combineBits(a, b, (x, y) => (x === y ? 0 : 1));
const andBits = (a: number, b: number) => combineBits(a, b, (x, y) => x * y);
const orBits = (a: number, b: number) =>
  combineBits(a, b, (x, y) => Math.max(x, y));

function unique(numbers: number[]): number[] {
  return [...new Set(numbers)].sort((a, b) => a - b);
}

const keyOf = (i: Implicant) => `${i.mask}:${i.value}`;

function mergeRound(entries: Implicant[]): {
  next: Implicant[];
  used: Set<string>;
} {
  const next = new Map<string, Implicant>();
  const used = new Set<string>();
  for (const [index, a] of entries.entries()) {
    for (const b of entries.slice(index + 1)) {
      if (a.mask !== b.mask) {
        continue;
      }
      const difference = xorBits(a.value, b.value);
      if (popcount(difference) !== 1) {
        continue;
      }
      const merged: Implicant = {
        value: andBits(a.value, b.value),
        mask: orBits(a.mask, difference),
        terms: unique([...a.terms, ...b.terms]),
      };
      next.set(keyOf(merged), merged);
      used.add(keyOf(a));
      used.add(keyOf(b));
    }
  }
  return { next: [...next.values()], used };
}

function sortImplicants(list: Implicant[]): Implicant[] {
  return [...list].sort(
    (a, b) =>
      popcount(a.value) - popcount(b.value) ||
      a.mask - b.mask ||
      a.value - b.value
  );
}

/** Every prime implicant of the function, with the rounds that produced them. */
export function primeImplicants(terms: number[]): {
  rounds: QmRound[];
  primes: Implicant[];
} {
  let current = sortImplicants(
    unique(terms).map((t) => ({ value: t, mask: 0, terms: [t] }))
  );
  const rounds: QmRound[] = [];
  const primes: Implicant[] = [];
  while (current.length > 0) {
    const { next, used } = mergeRound(current);
    rounds.push({
      entries: current.map((implicant) => ({
        implicant,
        combined: used.has(keyOf(implicant)),
      })),
    });
    for (const implicant of current) {
      if (!used.has(keyOf(implicant))) {
        primes.push(implicant);
      }
    }
    current = sortImplicants(next);
  }
  return {
    rounds,
    primes: primes.sort((a, b) => a.mask - b.mask || a.value - b.value),
  };
}

/** Literals in an implicant over n variables. */
export function literalCount(implicant: Implicant, n: number): number {
  return n - popcount(implicant.mask);
}

function isSuperset(big: number[], small: number[]): boolean {
  return small.every((x) => big.includes(x));
}

/** Expand a product of sums into its irredundant products (absorption applied). */
export function expandProductOfSums(sums: number[][]): number[][] {
  let products: number[][] = [[]];
  for (const sum of sums) {
    const grown = new Map<string, number[]>();
    for (const product of products) {
      for (const choice of sum) {
        const next = unique([...product, choice]);
        grown.set(next.join(","), next);
      }
    }
    const all = [...grown.values()];
    products = all.filter(
      (candidate) =>
        !all.some(
          (other) =>
            other !== candidate &&
            other.length <= candidate.length &&
            isSuperset(candidate, other) &&
            (other.length < candidate.length ||
              other.join(",") < candidate.join(","))
        )
    );
  }
  return products.sort(
    (a, b) => a.length - b.length || a.join(",").localeCompare(b.join(","))
  );
}

function chooseCover(
  covers: number[][],
  primes: Implicant[],
  n: number
): { cover: number[]; alternatives: number[][] } {
  const cost = (cover: number[]) => ({
    terms: cover.length,
    literals: cover.reduce((sum, i) => sum + literalCount(primes[i], n), 0),
  });
  let best = {
    terms: Number.POSITIVE_INFINITY,
    literals: Number.POSITIVE_INFINITY,
  };
  for (const cover of covers) {
    const c = cost(cover);
    if (
      c.terms < best.terms ||
      (c.terms === best.terms && c.literals < best.literals)
    ) {
      best = c;
    }
  }
  const alternatives = covers
    .filter((cover) => {
      const c = cost(cover);
      return c.terms === best.terms && c.literals === best.literals;
    })
    .sort((a, b) => a.join(",").localeCompare(b.join(",")));
  return { cover: alternatives[0] ?? [], alternatives };
}

/**
 * Minimal sum of products for the function that is 1 on `minterms`, free on `dontCares`
 * and 0 everywhere else, over `n` variables.
 */
export function minimize(
  n: number,
  minterms: number[],
  dontCares: number[]
): Minimization {
  const required = unique(minterms);
  const free = unique(dontCares).filter((d) => !required.includes(d));
  const { rounds, primes } = primeImplicants([...required, ...free]);
  const empty = { n, required, dontCares: free, rounds, primes };
  if (required.length === 0) {
    return {
      ...empty,
      essential: [],
      petrick: null,
      cover: [],
      alternatives: [[]],
      literals: 0,
    };
  }
  const coverers = (m: number) =>
    primes.flatMap((p, index) => (p.terms.includes(m) ? [index] : []));
  const essential = unique(
    required.flatMap((m) => {
      const options = coverers(m);
      return options.length === 1 ? options : [];
    })
  );
  const covered = new Set(essential.flatMap((i) => primes[i].terms));
  const remaining = required.filter((m) => !covered.has(m));
  let cover = essential;
  let alternatives = [essential];
  let petrick: PetrickResult | null = null;
  if (remaining.length > 0) {
    const sums = remaining.map((m) =>
      coverers(m).filter((i) => !essential.includes(i))
    );
    const products = expandProductOfSums(sums);
    petrick = { remaining, sums, products };
    const chosen = chooseCover(products, primes, n);
    cover = unique([...essential, ...chosen.cover]);
    alternatives = chosen.alternatives.map((alt) =>
      unique([...essential, ...alt])
    );
  }
  const literals = cover.reduce(
    (sum, i) => sum + literalCount(primes[i], n),
    0
  );
  return { ...empty, essential, petrick, cover, alternatives, literals };
}

// ---------------------------------------------------------------------------------------
// Writing the result
// ---------------------------------------------------------------------------------------

export type LiteralStyle = "prime" | "logic";

export const DEFAULT_NAMES = ["A", "B", "C", "D"];

function bitOf(value: number, n: number, k: number): number {
  return Math.floor(value / 2 ** (n - 1 - k)) % 2;
}

function negated(name: string, style: LiteralStyle): string {
  return style === "prime" ? `${name}′` : `¬${name}`;
}

/** The AND-term of an implicant: `A′BD`, `¬A ∧ B ∧ D`, or `1` when every variable dropped out. */
export function productTerm(
  implicant: Implicant,
  n: number,
  names: readonly string[],
  style: LiteralStyle
): string {
  const lits: string[] = [];
  for (let k = 0; k < n; k += 1) {
    if (bitOf(implicant.mask, n, k) === 1) {
      continue;
    }
    lits.push(
      bitOf(implicant.value, n, k) === 1 ? names[k] : negated(names[k], style)
    );
  }
  if (lits.length === 0) {
    return "1";
  }
  return lits.join(style === "prime" ? "" : " ∧ ");
}

/** The OR-clause of an implicant of the complement: `A + B′`, or `0` when empty. */
export function sumClause(
  implicant: Implicant,
  n: number,
  names: readonly string[],
  style: LiteralStyle
): string {
  const lits: string[] = [];
  for (let k = 0; k < n; k += 1) {
    if (bitOf(implicant.mask, n, k) === 1) {
      continue;
    }
    // De Morgan: a product term of the complement becomes a sum of opposite literals.
    lits.push(
      bitOf(implicant.value, n, k) === 1 ? negated(names[k], style) : names[k]
    );
  }
  if (lits.length === 0) {
    return "0";
  }
  return lits.join(style === "prime" ? " + " : " ∨ ");
}

/** `A′B + CD`, with the constant 0 or 1 for the trivial functions. */
export function sumOfProducts(
  min: Minimization,
  cover: number[],
  names: readonly string[],
  style: LiteralStyle
): string {
  if (cover.length === 0) {
    return "0";
  }
  const terms = cover.map((i) =>
    productTerm(min.primes[i], min.n, names, style)
  );
  if (terms.includes("1")) {
    return "1";
  }
  const wrap = style === "logic" && terms.length > 1 && min.n > 1;
  return terms
    .map((term) => (wrap && term.includes("∧") ? `(${term})` : term))
    .join(style === "prime" ? " + " : " ∨ ");
}

/** The product of sums built from a minimisation of the complement. */
export function productOfSums(
  complement: Minimization,
  cover: number[],
  names: readonly string[],
  style: LiteralStyle
): string {
  if (cover.length === 0) {
    return "1";
  }
  const clauses = cover.map((i) =>
    sumClause(complement.primes[i], complement.n, names, style)
  );
  if (clauses.includes("0")) {
    return "0";
  }
  if (clauses.length === 1) {
    return clauses[0];
  }
  const joiner = style === "prime" ? "" : " ∧ ";
  // A clause with one literal needs no brackets.
  const single = style === "prime" ? " + " : " ∨ ";
  return clauses
    .map((clause) => (clause.includes(single) ? `(${clause})` : clause))
    .join(joiner);
}

/** The pattern of an implicant as written in the tables: `01-1`. */
export function patternOf(implicant: Implicant, n: number): string {
  let text = "";
  for (let k = 0; k < n; k += 1) {
    text +=
      bitOf(implicant.mask, n, k) === 1
        ? "-"
        : String(bitOf(implicant.value, n, k));
  }
  return text;
}

/** Minterms where `cover` is 1 (don't-care cells included when a group uses them). */
export function coverValue(
  min: Minimization,
  cover: number[],
  minterm: number
): boolean {
  return cover.some((i) => min.primes[i].terms.includes(minterm));
}
